import { state } from '../lib/state.js';
import { supabase } from '../lib/supabase.js';
import { isSupabaseConfigured } from '../config.js';
import { ensureOrganization } from '../services/repository.js';
import { resumeServerCalculatorJobs } from '../services/server-calculator-bridge.js';
import { markInactivityActivity } from '../services/inactivity-logout.js';
import { els } from './dom.js';
import { requestRender } from './events.js';
import { reloadRoutes } from '../routes/route-data.js';
import { setStatus, setBadge } from '../lib/app-helpers.js';

let authTransitionId = 0;
let automaticLogoutMessage = '';
let protectedAppStarter = () => {};

export function configureAuth({ startProtectedApp } = {}) {
  if (typeof startProtectedApp === 'function') protectedAppStarter = startProtectedApp;
}

export async function initialize() {
  showLoginGate('Sprawdzam aktywną sesję…', 'working');

  if (!isSupabaseConfigured || !supabase) {
    showLoginGate('Brak konfiguracji Supabase. Dostęp do aplikacji jest zablokowany.', 'error');
    return;
  }

  const { data:{ session }, error } = await supabase.auth.getSession();
  if (error) {
    console.warn('[D24 AUTH] Nie udało się odczytać sesji:', error);
    showLoginGate(`Nie udało się odczytać sesji: ${error.message}`, 'error');
  } else {
    await applySession(session || null);
  }

  supabase.auth.onAuthStateChange((event, next) => {
    if (event === 'TOKEN_REFRESHED' && next?.access_token) {
      state.session = next;
      return;
    }
    if (event === 'SIGNED_IN' && next?.access_token) {
      // Prawdziwe logowanie użytkownika rozpoczyna nowy okres aktywności.
      // Samo odtworzenie starej sesji po ponownym otwarciu aplikacji
      // nie może zerować 60-minutowego licznika bezczynności.
      markInactivityActivity();
    }
    applySession(next || null).catch(error => {
      console.error('[D24 AUTH]', error);
      showLoginGate(`Błąd sesji: ${error.message}`, 'error');
    });
  });
}


export function showLoginGate(message = 'Zaloguj się, aby przejść do aplikacji.', kind = 'neutral') {
  document.body.classList.add('auth-locked');
  els.appShell?.classList.add('hidden');
  els.appShell?.setAttribute('aria-hidden', 'true');
  els.loginScreen?.classList.remove('hidden');
  els.loginScreen?.setAttribute('aria-hidden', 'false');
  if (message) setStatus(els.authStatus, message, kind);
  window.setTimeout(() => els.authEmail?.focus(), 0);
}

export function showProtectedApp(session) {
  els.loginScreen?.classList.add('hidden');
  els.loginScreen?.setAttribute('aria-hidden', 'true');
  els.appShell?.classList.remove('hidden');
  els.appShell?.setAttribute('aria-hidden', 'false');
  document.body.classList.remove('auth-locked');

  if (els.currentUserLabel) {
    els.currentUserLabel.textContent = session?.user?.email || '';
  }

  // Obsługuje zarówno nowy przycisk #signOutBtn, jak i starszy
  // przycisk #authToggleBtn pozostawiony w index.html.
  if (els.signOutBtn) {
    els.signOutBtn.type = 'button';
    els.signOutBtn.setAttribute('aria-label', 'Wyloguj z aplikacji');
    els.signOutBtn.setAttribute('title', 'Wyloguj');
    els.signOutBtn.classList.remove('hidden', 'is-busy');
    els.signOutBtn.disabled = false;
  }
}

export function closeProtectedUi() {
  document.querySelectorAll('.modal-backdrop').forEach(element => {
    element.classList.add('hidden');
    element.setAttribute('aria-hidden', 'true');
  });
  document.body.style.overflow = '';
}


export async function requireAuthenticatedSession(actionLabel = 'wykonać tę operację') {
  if (!isSupabaseConfigured || !supabase) {
    showLoginGate('Brak konfiguracji Supabase. Dostęp do aplikacji jest zablokowany.', 'error');
    return false;
  }

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    state.session = null;
    state.organizationId = null;
    showLoginGate(`Nie udało się sprawdzić sesji Supabase: ${error.message}`, 'error');
    requestRender();
    return false;
  }

  const session = data?.session || null;
  if (!session?.access_token) {
    state.session = null;
    state.organizationId = null;
    showLoginGate(`Musisz się zalogować, aby ${actionLabel}.`, 'error');
    requestRender();
    return false;
  }

  state.session = session;
  if (!state.organizationId) {
    try {
      state.organizationId = await ensureOrganization();
    } catch (organizationError) {
      showLoginGate(`Nie udało się przygotować organizacji użytkownika: ${organizationError.message}`, 'error');
      requestRender();
      return false;
    }
  }

  return true;
}


export async function authAction() {
  if (!supabase) {
    showLoginGate('Brak konfiguracji Supabase.', 'error');
    return;
  }

  const email = String(els.authEmail?.value || '').trim();
  const password = String(els.authPassword?.value || '');
  if (!email || password.length < 6) {
    setStatus(els.authStatus, 'Podaj e-mail i hasło (minimum 6 znaków).', 'error');
    return;
  }

  els.signInBtn.disabled = true;
  setStatus(els.authStatus, 'Trwa logowanie…', 'working');
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setStatus(els.authStatus, 'Nieprawidłowy e-mail lub hasło.', 'error');
      return;
    }

    const session = data?.session || null;
    if (!session?.access_token) {
      setStatus(els.authStatus, 'Nie udało się utworzyć aktywnej sesji.', 'error');
      return;
    }

    markInactivityActivity();
    await applySession(session);
    if (els.authPassword) els.authPassword.value = '';
  } catch (error) {
    console.error('[D24 AUTH] Logowanie nie powiodło się:', error);
    setStatus(els.authStatus, `Logowanie nie powiodło się: ${error.message}`, 'error');
  } finally {
    els.signInBtn.disabled = false;
  }
}


export async function handleAutomaticLogout() {
  automaticLogoutMessage = 'Wylogowano automatycznie po 60 minutach bezczynności.';
  await applySession(null);
}

export async function applySession(session) {
  const transitionId = ++authTransitionId;
  state.session = session || null;

  if (!session?.access_token) {
    state.organizationId = null;
    state.routes = [];
    closeProtectedUi();
    if (els.currentUserLabel) els.currentUserLabel.textContent = '';
    setBadge(els.connectionBadge, 'Supabase – niezalogowany', 'neutral');
    showLoginGate(
      automaticLogoutMessage || 'Zaloguj się, aby przejść do aplikacji.',
      automaticLogoutMessage ? 'warning' : 'neutral'
    );
    requestRender();
    return;
  }

  automaticLogoutMessage = '';
  showLoginGate('Przygotowuję konto i dane użytkownika…', 'working');
  try {
    state.organizationId = await ensureOrganization();
    if (transitionId !== authTransitionId) return;
    showProtectedApp(session);
    protectedAppStarter();
    setBadge(els.connectionBadge, 'Połączono', 'ok');
    resumeServerCalculatorJobs(state.organizationId).catch(error => {
      console.warn('[D24 SERVER CALCULATOR] Nie udało się wznowić obserwowania zadań:', error);
    });
    await reloadRoutes();
    if (transitionId !== authTransitionId) return;
    requestRender();
  } catch (error) {
    console.error('[D24 AUTH] Nie udało się przygotować konta:', error);
    state.organizationId = null;
    state.routes = [];
    showLoginGate(`Nie udało się przygotować konta w Supabase: ${error.message}`, 'error');
    setBadge(els.connectionBadge, 'Supabase – błąd konta', 'error');
    requestRender();
  }
}
